import type { AlgorithmOperation } from "@algoquest/schemas";
import { BubbleSortEngine, type BubbleSortDomainState } from "./bubble-sort";
import type { SortActionOutcome, SortController, SortDecision, SortViewState } from "./SortController";

/**
 * Phase 08 — wraps BubbleSortEngine behind the shared SortController
 * contract, replacing GameRuntime's old `tryInspectBubblePair` /
 * `tryDecideBubble` / `applyBubbleSortStateToBoxes` / `updateBubbleComparePointer`
 * methods (their guard logic is reproduced here verbatim, just returning
 * a SortActionOutcome instead of mutating GameRuntime state directly).
 *
 * toView() translates the engine's own domain state into SortViewState:
 * `requiredIndex`/`compareIndices` mirror the engine's own pointer/
 * pointer+1 pair (the right index is clamped to the last array slot,
 * matching the old compare-pointer positioning logic); `stickyIndex` is
 * always null — Bubble Sort has no candidate carried across a round, see
 * `SortViewState`'s own doc comment; `shifts` is always 0 (Master Spec
 * §40 tracks shifts only for Insertion Sort).
 */
function toView(state: BubbleSortDomainState): SortViewState {
  const rightIndex = Math.min(state.pointer + 1, state.values.length - 1);
  return {
    algorithm: "bubble-sort",
    values: state.values,
    sortDirection: state.sortDirection,
    requiredIndex: state.pointer,
    compareIndices: [state.pointer, rightIndex],
    stickyIndex: null,
    awaitingDecision: state.awaitingDecision,
    sortedIndices: state.sortedIndices,
    comparisons: state.comparisons,
    swaps: state.swaps,
    shifts: 0,
    passesCompleted: state.passesCompleted,
    mistakes: state.mistakes,
    isComplete: state.isComplete,
  };
}

export class BubbleSortController implements SortController {
  readonly algorithm = "bubble-sort" as const;
  private readonly engine: BubbleSortEngine;

  constructor(values: number[], sortDirection: "ascending" | "descending") {
    this.engine = new BubbleSortEngine(values, sortDirection);
  }

  getView(): SortViewState {
    return toView(this.engine.getState());
  }

  getTrace(): AlgorithmOperation[] {
    return this.engine.getTrace();
  }

  handlePrimaryAction(nearestIndex: number | null): SortActionOutcome {
    const state = this.engine.getState();
    if (state.isComplete || state.awaitingDecision || nearestIndex === null) {
      return { kind: "ignored" };
    }
    if (state.pointer !== nearestIndex) {
      return { kind: "rejected-wrong-box", expectedIndex: state.pointer };
    }
    const result = this.engine.inspect(performance.now());
    if (!result) {
      return { kind: "ignored" };
    }
    return { kind: "accepted", result: { view: toView(result.state), operations: result.operations } };
  }

  handleDecision(decision: SortDecision): SortActionOutcome {
    const before = this.engine.getState();
    if (!before.awaitingDecision) {
      return { kind: "ignored" };
    }
    const choice: "swap" | "keep" = decision === "advance" ? "swap" : "keep";
    const { wasCorrect, result } = this.engine.decide(choice, performance.now());
    if (!wasCorrect || !result) {
      return { kind: "rejected-wrong-decision" };
    }
    return { kind: "accepted", result: { view: toView(result.state), operations: result.operations } };
  }
}
